import React, { useContext, useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import CreditcardFilledIcon from '@atlaskit/icon/glyph/creditcard-filled';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import { token } from '@atlaskit/tokens';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import OpenIcon from '@atlaskit/icon/glyph/editor/open';
import { N800 } from '@atlaskit/theme/colors';

import { ConfluenceEdition } from '@confluence/change-edition';
import {
	PremiumAdminOnboarding,
	PremiumTrialOnboardingModal,
} from '@confluence/premium-onboarding';
import { SPAViewContext } from '@confluence/spa-view-context';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from '@confluence/premium-onboarding/entry-points/useIsPremiumOnboardingFeatureDiscoveryEnabled';
import { TriggerFrom } from '@confluence/premium-onboarding/entry-points/PremiumTrialOnboardingModal';
import { useCanAddBillingForTrialProductUser } from '@confluence/experiment-more-product-users-can-upgrade-pay';
import { useAutoUpgradeAwareness } from '@confluence/experiment-auto-upgrade-awareness';

import { useBillingUrls } from '../useBillingUrls';
import { useReverseTrialCohort, useReverseTrialEligible } from '../useReverseTrialEligible';
import { getTrialDaysLeft } from '../graphql/getTrialDaysLeftFromQuery';

import { getPillType } from './getPillType';
import { EXPLORE_STANDARD_FEATURES_LINK } from './ActionMenu';
import { PremiumMenuHighlight } from './PremiumMenuHighlight';
import { PremiumInsightsButton } from './PremiumInsightsButton';
import { PopupMenuButton, PopupMenuButtonText } from './PopupMenuButton';

//  Different individual Popup Menu items
const noop = () => {};

const i18n = defineMessages({
	reviewFeatureGuide: {
		id: 'trial-components.premium.review-feature-guide.button',
		defaultMessage: 'Review feature guide',
		description: 'Review feature guide button',
	},
	chargeMessage: {
		id: 'trial-components.premium.charge-message',
		defaultMessage:
			'You’ll be charged the Premium monthly cost when your trial ends on {trialEndDate}.',
		description: 'Message for site that is in Premium trial and has payment info on file',
	},
	exploreStandardFeatures: {
		id: 'trial-components.explore.standard.button',
		defaultMessage: 'Explore Standard features',
		description: 'Explore standard features button',
	},
	explorePremiumFeatures: {
		id: 'trial-components.explore.premium.button',
		defaultMessage: 'Explore Premium features',
		description: 'Explore premium features button',
	},
	addStandardPaymentInfo: {
		id: 'trial-components.add.payment.standard.button',
		defaultMessage: 'Add payment details to stay on Standard',
		description: 'Add payment details to stay on Standard',
	},
	addPremiumPaymentInfo: {
		id: 'trial-components.add.payment.premium.button',
		defaultMessage: 'Add payment details to stay on Premium',
		description: 'Add payment details to stay on Premium',
	},
	seeBillingInfo: {
		id: 'trial-components.see.billing.button',
		defaultMessage: 'See billing details',
		description: 'See billing details once user has added billing info',
	},
	endTrial: {
		id: 'trial-components.end.trial.button',
		defaultMessage: 'End trial and change plans',
		description: 'End trial and change plans',
	},
	downgradeMessage: {
		id: 'trial-components.action.menu.downgrade.message',
		defaultMessage:
			// eslint-disable-next-line no-restricted-syntax
			"After your trial, we'll downgrade you to Free. <link>Learn more</link>",
		description: "Warning users they'll be downgraded to free when their trial ends",
	},
	downgradeMessageV2: {
		id: 'trial-components.action.menu.downgrade.message.v2',
		defaultMessage: 'After your trial, we’ll downgrade you to Free.',
		description: "Warning users they'll be downgraded to free when their trial ends",
	},
	learnMoreAriaLabel: {
		id: 'trial-components.action.menu.learn.more.aria.label',
		defaultMessage: 'Learn more about removing users and downgrading to Confluence Free',
		description: 'Aria label for a learn more link to information about downgrading',
	},
});

const LEARN_MORE_DOWNGRADE_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/removing-users-and-downgrading-to-confluence-free/';

export interface ActionMenuButtonsType {
	edition: ConfluenceEdition;
	hasBillingInformation: boolean;
	isPreDunningPeriod: boolean;
	trialEndDate?: string;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconPositioningAdjuster = styled.div({
	width: '24px',
	height: '24px',
	marginRight: token('space.negative.050', '-4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupMenuFooterContainer = styled.div<{
	isReverseTrialEnrolledVersion?: boolean;
}>({
	margin: '0',
	display: 'flex',
	alignItems: 'center',
	alignSelf: 'stretch',
	fontWeight: '400',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isReverseTrialEnrolledVersion }) =>
		isReverseTrialEnrolledVersion
			? token('color.text.subtle', '#44546F')
			: token('color.text', N800),
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: ({ isReverseTrialEnrolledVersion }) =>
		isReverseTrialEnrolledVersion
			? `${token('space.100', '8px')} ${token('space.200', '16px')}`
			: `${token('space.100', '8px')} ${token('space.250', '20px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	fontSize: ({ isReverseTrialEnrolledVersion }) =>
		isReverseTrialEnrolledVersion ? '12px' : '11px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	lineHeight: ({ isReverseTrialEnrolledVersion }) =>
		isReverseTrialEnrolledVersion ? '12px' : '14px',
});

export const ActionMenuButtons: FC<ActionMenuButtonsType> = ({
	edition,
	hasBillingInformation,
	isPreDunningPeriod,
	trialEndDate,
}) => {
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const { canAddBillingForTrial, analyticsAttributes: canAddBillingForTrialAnalyticsAttributes } =
		useCanAddBillingForTrialProductUser();
	const { paymentFlowUrl, changePlanUrl } = useBillingUrls();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
	const { isReverseTrialEligible } = useReverseTrialEligible();
	const { isReverseTrialEnrolledVersion } = useReverseTrialCohort();
	const { auaAnalyticAttributes } = useAutoUpgradeAwareness();

	const downgradeMessage = isReverseTrialEnrolledVersion
		? i18n.downgradeMessageV2
		: i18n.downgradeMessage;
	const { isPremiumOnboardingFeatureDiscoveryEnabled, isPTORTEnabled } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled({
			shouldCheckIsInTrial: false,
		});
	const { isSiteAdmin } = useContext(SPAViewContext);
	const pillType = getPillType({ isSiteAdmin, hasBillingInformation });

	const { formatMessage, locale } = useIntl();
	const formattedIntlTrialEndDate =
		trialEndDate &&
		new Intl.DateTimeFormat(locale, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			timeZone: 'UTC',
		}).format(new Date(trialEndDate));

	const fireButtonEvent = (triggeredFrom: string) => {
		const attributes = {
			triggeredFrom,
			isPreDunningPeriod,
			...(canAddBillingForTrial && canAddBillingForTrialAnalyticsAttributes),
			...(!isPremium && auaAnalyticAttributes),
		};
		isPremiumOnboardingFeatureDiscoveryEnabled &&
			Object.assign(attributes, {
				pillType,
				trialDaysLeft: getTrialDaysLeft(trialEndDate),
				isPTORTEnabled,
			});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: isPremium ? 'premTrialActionScreen' : 'stanTrialActionScreen',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: isPremium ? 'premTrialActionComponent' : 'stanTrialActionComponent',
				attributes,
			},
		}).fire();
	};

	const onClickExploreStandard = () => {
		fireButtonEvent('Explore Standard features');
		window.open(EXPLORE_STANDARD_FEATURES_LINK, '_blank', 'noreferrer');
	};

	const onClickBilling = () => {
		// @todo Update analyticsLinkText to match buttonText in the future.
		// Currently, this change is deferred to avoid altering data collection metrics.
		const isPremiumText = isPremium ? 'Premium' : 'Standard';
		const analyticsLinkText = hasBillingInformation
			? 'See billing details'
			: `Add billing info to keep ${isPremiumText}`;

		const url = hasBillingInformation ? changePlanUrl : paymentFlowUrl;

		window.open(url, '_blank', 'noreferrer');
		fireButtonEvent(analyticsLinkText);
	};

	const onClickEndTrial = () => {
		window.open(changePlanUrl, '_blank', 'noreferrer');
		fireButtonEvent('End trial and change plans');
	};

	const premiumFeatureButtonI18n = isPremiumOnboardingFeatureDiscoveryEnabled
		? i18n.reviewFeatureGuide
		: i18n.explorePremiumFeatures;

	const featureButtonI18n = isPremium ? premiumFeatureButtonI18n : i18n.exploreStandardFeatures;

	const onClickExplorePremium = () => {
		const triggeredFrom = isPremiumOnboardingFeatureDiscoveryEnabled
			? 'Review feature list'
			: 'Explore Premium features';
		fireButtonEvent(triggeredFrom);
		setIsPremiumModalOpen(true);
	};

	return (
		<div>
			{isPremium &&
				isPremiumModalOpen &&
				(isPremiumOnboardingFeatureDiscoveryEnabled ? (
					<PremiumTrialOnboardingModal
						onClose={() => setIsPremiumModalOpen(false)}
						triggerFrom={TriggerFrom.PILL}
						daysLeft={getTrialDaysLeft(trialEndDate) || 0}
					/>
				) : (
					<PremiumAdminOnboarding stop={noop} />
				))}
			<PopupMenuButton
				isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}
				iconBefore={
					<PremiumIcon
						label=""
						testId="Premium"
						size="small"
						primaryColor={token('color.icon', '#44546f')}
					/>
				}
				iconAfter={
					isReverseTrialEnrolledVersion ? (
						<IconPositioningAdjuster>
							<OpenIcon
								label=""
								testId="ChevronRight"
								size="medium"
								primaryColor={token('color.icon', '#44546F')}
							/>
						</IconPositioningAdjuster>
					) : (
						<ChevronRightIcon
							label=""
							testId="ChevronRight"
							size="medium"
							primaryColor={token('color.icon', '#44546F')}
						/>
					)
				}
				onClick={isPremium ? onClickExplorePremium : onClickExploreStandard}
				aria-haspopup
			>
				<PopupMenuButtonText>
					<FormattedMessage {...featureButtonI18n} />
				</PopupMenuButtonText>
			</PopupMenuButton>
			<PremiumInsightsButton
				trialEndDate={trialEndDate}
				isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}
			/>
			{/* By default, the payment button is shown regardless if payment is provided and the entire menu is visible to site admin only.
        For premium onboarding feature discovery experiment,the paymenet button is shown when payment is not provided and the user is site admin (the entire menu is visible to both site admin and non site admin ) */}
			{(!isPremiumOnboardingFeatureDiscoveryEnabled ||
				((isSiteAdmin || canAddBillingForTrial) && !hasBillingInformation)) && (
				<PopupMenuButton
					isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}
					iconBefore={
						<CreditcardFilledIcon
							label=""
							testId="CreditCardFilled"
							size="small"
							primaryColor={token('color.icon', '#44546f')}
						/>
					}
					iconAfter={
						isReverseTrialEnrolledVersion ? (
							<ShortcutIcon
								label=""
								testId="ChevronRight"
								size="small"
								primaryColor={token('color.icon', '#44546F')}
							/>
						) : (
							<ChevronRightIcon
								label=""
								testId="ChevronRight"
								size="medium"
								primaryColor={token('color.icon', '#44546F')}
							/>
						)
					}
					onClick={onClickBilling}
				>
					<PopupMenuButtonText>
						{hasBillingInformation ? (
							<FormattedMessage {...i18n.seeBillingInfo} />
						) : (
							<FormattedMessage
								{...(isPremium ? i18n.addPremiumPaymentInfo : i18n.addStandardPaymentInfo)}
							/>
						)}
					</PopupMenuButtonText>
				</PopupMenuButton>
			)}
			{isSiteAdmin && (isReverseTrialEligible || isPremiumOnboardingFeatureDiscoveryEnabled) && (
				<PopupMenuButton
					isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}
					iconBefore={
						<RecentIcon
							label=""
							testId="Recent"
							size="small"
							primaryColor={token('color.icon', '#44546f')}
						/>
					}
					iconAfter={
						isReverseTrialEnrolledVersion ? (
							<ShortcutIcon
								label=""
								testId="ChevronRight"
								size="small"
								primaryColor={token('color.icon', '#44546F')}
							/>
						) : (
							<ChevronRightIcon
								label=""
								testId="ChevronRight"
								size="medium"
								primaryColor={token('color.icon', '#44546F')}
							/>
						)
					}
					onClick={onClickEndTrial}
				>
					<PopupMenuButtonText>
						<FormattedMessage {...i18n.endTrial} />
					</PopupMenuButtonText>
				</PopupMenuButton>
			)}
			{isPremiumOnboardingFeatureDiscoveryEnabled && (
				<PremiumMenuHighlight
					hasBillingInformation={hasBillingInformation}
					isPTORTEnabled={isPTORTEnabled}
				/>
			)}
			{!hasBillingInformation && (isSiteAdmin || canAddBillingForTrial) && (
				<PopupMenuFooterContainer isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}>
					<FormattedMessage
						{...downgradeMessage}
						values={{
							/* eslint-disable-next-line react/no-unstable-nested-components */
							link: (chunks: React.ReactNode[]) => (
								<a
									href={LEARN_MORE_DOWNGRADE_LINK}
									target="_blank"
									aria-label={formatMessage(i18n.learnMoreAriaLabel)}
									onClick={() => {
										isPremiumOnboardingFeatureDiscoveryEnabled &&
											createAnalyticsEvent({
												type: 'sendUIEvent',
												data: {
													action: 'clicked',
													actionSubject: 'link',
													actionSubjectId: 'premTrialActionComponentLearnMore',
													source: 'premTrialActionComponent',
													attributes: {
														...canAddBillingForTrialAnalyticsAttributes,
														isPTORTEnabled,
													},
												},
											}).fire();
									}}
								>
									{chunks}
								</a>
							),
						}}
					/>
				</PopupMenuFooterContainer>
			)}
			{isPremiumOnboardingFeatureDiscoveryEnabled && isSiteAdmin && hasBillingInformation && (
				<PopupMenuFooterContainer>
					<FormattedMessage
						{...i18n.chargeMessage}
						values={{
							trialEndDate: formattedIntlTrialEndDate,
						}}
					/>
				</PopupMenuFooterContainer>
			)}
		</div>
	);
};
